import React, { useState } from 'react';
import { createStore } from '@/lib/supabase/stores';
import { normalizeLettersOnlySlug, isLettersOnly, isReservedName } from '@/lib/utils';
import { supabase } from '@/lib/supabase/client';
import { uploadStoreAvatar } from '@/lib/supabase/images';
import { CheckCircle, X, Loader2 } from '@/components/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from '@/components/ui/card';
import ImageManagerDialog from './ImageManagerDialog';
import { useSession } from '@/context/SessionContext';
import { useToast } from '@/components/ui/use-toast';
import { extractCityFromDisplayName, maybeAwardFounderBadge } from '@/lib/founderProgram';

export default function CreateStoreForm({ onCreated }: { onCreated?: (s: any) => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedGalleryUrl, setSelectedGalleryUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugValid, setSlugValid] = useState<boolean | null>(null);
  const [slugStatus, setSlugStatus] = useState<'checking' | 'available' | 'unavailable' | 'reserved' | null>(null);
  const [slugMessage, setSlugMessage] = useState<string | null>(null);

  React.useEffect(() => {
    // Auto-fill slug from name only if the user hasn't edited slug manually
    if (!slugTouched) {
      setSlug(normalizeLettersOnlySlug(name));
      setSlugValid(null);
    }
  }, [name, slugTouched]);

  // Debounced availability check (profiles + stores + blacklist)
  React.useEffect(() => {
    let mounted = true;
    let timer: any = null;
    const doCheck = async () => {
      const candidate = slug || normalizeLettersOnlySlug(name || '');
      if (!candidate || candidate.length === 0) {
        if (mounted) { setSlugStatus(null); setSlugMessage(null); }
        return;
      }
      if (isReservedName(candidate)) {
        if (mounted) { setSlugStatus('reserved'); setSlugMessage('Reservado'); }
        return;
      }
      if (!isLettersOnly(candidate) || candidate.length < 3 || candidate.length > 30) {
        if (mounted) { setSlugStatus('unavailable'); setSlugMessage('Inválido (solo letras, 3–30)'); }
        return;
      }
      if (mounted) { setSlugStatus('checking'); setSlugMessage('Comprobando...'); }
      try {
        const { data: pData, error: pErr } = await supabase.from('profiles').select('id').eq('username', candidate).maybeSingle();
        if (pErr) console.warn('profile check error', pErr);
        if (pData && pData.id) {
          if (mounted) { setSlugStatus('unavailable'); setSlugMessage('Ya en uso por un usuario'); }
          return;
        }
        const { data: sData, error: sErr } = await supabase.from('stores').select('id').eq('slug', candidate).maybeSingle();
        if (sErr) console.warn('store check error', sErr);
        if (sData && sData.id) {
          if (mounted) { setSlugStatus('unavailable'); setSlugMessage('Ya en uso por una tienda'); }
          return;
        }
        if (mounted) { setSlugStatus('available'); setSlugMessage('Disponible'); }
      } catch (err) {
        console.error('slug check failed', err);
        if (mounted) { setSlugStatus('unavailable'); setSlugMessage('Error comprobando'); }
      }
    };
    timer = setTimeout(doCheck, 800);
    return () => {
      mounted = false;
      clearTimeout(timer);
      // clear transient checking state so spinner doesn't remain when request is canceled
      setSlugStatus(null);
      setSlugMessage(null);
    };
  }, [slug, name]);

  // validate slug when it changes (if touched)
  React.useEffect(() => {
    if (!slugTouched) return;
    if (!slug || slug.trim().length === 0) {
      setSlugValid(null);
      return;
    }
    // must be letters only, 3..30
    // Reserved names check
    if (isReservedName(slug)) {
      setSlugValid(false);
      return;
    }
    const ok = isLettersOnly(slug);
    if (!ok) {
      setSlugValid(false);
      return;
    }
    if (slug.length < 3 || slug.length > 30) {
      setSlugValid(false);
      return;
    }
    setSlugValid(true);
  }, [slug, slugTouched]);
  const { session, relevantLocation } = useSession();
  const { toast } = useToast();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const generated = normalizeLettersOnlySlug(name) || '';
      const payload: any = { name, slug: slug || generated, description };
      if (relevantLocation?.displayName && Number.isFinite(relevantLocation.latitude) && Number.isFinite(relevantLocation.longitude)) {
        payload.location_display_name = relevantLocation.displayName;
        payload.latitude = relevantLocation.latitude;
        payload.longitude = relevantLocation.longitude;
      }
      if (selectedGalleryUrl) {
        payload.avatar_url = selectedGalleryUrl;
      } else if (avatarFile) {
        try {
          const userId = session?.user?.id || '';
          const url = await uploadStoreAvatar(avatarFile, userId);
          payload.avatar_url = url;
        } catch (imgErr) {
          console.warn('Could not upload avatar, continuing without avatar', imgErr);
        }
      }
      const store = await createStore(payload);
      try {
        window.dispatchEvent(new CustomEvent('store-created', { detail: { store } }));
      } catch {
        // Ignore dispatch errors in constrained environments
      }

      const awardedBadge = await maybeAwardFounderBadge({
        userId: session?.user?.id,
        storeId: store?.id,
        storeLocationDisplayName: store?.location_display_name || payload.location_display_name || null,
        fallbackCity: relevantLocation?.displayName ? extractCityFromDisplayName(relevantLocation.displayName) : null,
      });

      if (awardedBadge) {
        toast({
          title: `Badge desbloqueado: Fundador ${awardedBadge.city}`,
          description: 'Creaste la primera tienda durante el desafío. Esta ciudad ahora entra al catálogo predefinido.',
        });
        try {
          window.dispatchEvent(new CustomEvent('founder-badge-awarded', { detail: awardedBadge }));
        } catch {
          // Ignore dispatch errors in constrained environments
        }
      }

      setName(''); setSlug(''); setDescription('');
      setAvatarFile(null); setAvatarPreview(null);
      if (onCreated) onCreated(store);
    } catch (err: any) {
      console.error('CreateStoreForm error', err);
      setError(err?.message || 'Error creando tienda');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="relative flex flex-col space-y-1.5 p-6">
        <CardTitle>Crear Tienda</CardTitle>
        <CardDescription>Crea una tienda para agrupar tus productos y administrar miembros.</CardDescription>
      </CardHeader>
      <form onSubmit={submit}>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Nombre</label>
              <Input value={name} onChange={e => setName(e.target.value)} required placeholder="Nombre de la tienda" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Slug</label>
              <div className="flex items-center">
                <span className="text-sm text-muted-foreground select-none mr-2">minibid.org/</span>
                <div className="relative flex-1">
                  <Input
                    value={slug}
                    onChange={e => { setSlug(e.target.value); setSlugTouched(true); }}
                    placeholder=""
                    className="pr-10"
                  />
                  {/* status icon */}
                  {slugStatus === 'available' && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600" aria-hidden />
                  )}
                  {slugStatus === 'unavailable' && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-600" aria-hidden />
                  )}
                  {slugStatus === 'reserved' && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-600" aria-hidden />
                  )}
                  {slugStatus === 'checking' && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
                  )}
                </div>
              </div>
              <p className="text-xs mt-1">
                {slugStatus === 'checking' ? (
                  <span className="inline-block align-middle"><Skeleton className="h-4 w-32" /></span>
                ) : (
                  <span className="text-muted-foreground">{slugMessage || 'Solo letras, 3–30 caracteres. Evita nombres reservados.'}</span>
                )}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Descripción</label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripción breve" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Avatar</label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const f = e.target.files && e.target.files[0];
                    if (f) {
                      setAvatarFile(f);
                      setSelectedGalleryUrl(null);
                      try {
                        const url = URL.createObjectURL(f);
                        setAvatarPreview(url);
                      } catch (_) {
                        setAvatarPreview(null);
                      }
                    } else {
                      setAvatarFile(null);
                      setAvatarPreview(null);
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={() => setIsGalleryOpen(true)}>Seleccionar de galería</Button>
              </div>
              {selectedGalleryUrl && (
                <img src={selectedGalleryUrl} alt="avatar selected" className="mt-2 h-20 w-20 rounded-md object-cover" />
              )}
              {avatarPreview && !selectedGalleryUrl && <img src={avatarPreview} alt="avatar preview" className="mt-2 h-20 w-20 rounded-md object-cover" />}
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
          </div>
        </CardContent>
        <CardFooter>
            <div className="w-full flex items-center justify-end">
            <Button type="submit" disabled={loading || (slugTouched && slugValid === false) || slugStatus === 'unavailable' || slugStatus === 'reserved'}>{loading ? 'Creando...' : 'Crear Tienda'}</Button>
          </div>
        </CardFooter>
      </form>
        {session && (
          <ImageManagerDialog session={session} isOpen={isGalleryOpen} onClose={() => setIsGalleryOpen(false)} onSelect={(url) => { setSelectedGalleryUrl(url as string); setAvatarPreview(null); setIsGalleryOpen(false); }} allowUpload={true} allowMultipleSelect={false} />
        )}
    </Card>
  );
}
